import { IonRouterLink } from "@ionic/react";
import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ExpandableSection from "@components/animations/ExpandableSection";
import Button from "@components/buttons/Button";
import BasicCard from "@components/cards/BasicCard";
import styles from "@components/carePlan/CarePlan.module.css";
import Dot from "@components/Dot";
import ExpertAvatar from "@components/ExpertAvatar";
import TaskPopover from "@components/TaskPopover";
import Icon from "@components/text/Icon";
import IconBase from "@components/text/IconBase";
import StatusText from "@components/text/StatusText";
import Tooltip from "@components/Tooltip";
import BodyCopy from "@components/typography/BodyCopy";
import useMyClient from "@hooks/useMyClient";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import { JourneyStatusEnum } from "@typing/Enums";
import { GetJourneyWithPlanQuery, GoalStatusEnum, TaskFragment } from "@typing/Generated";
import { colorForStatus, resourceHasChanges, sortByPosition, taskHasChanges } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Task = TaskFragment;

type Props = {
  handleEdit: (task: Task) => void;
  hover?: boolean;
  journey: GetJourneyWithPlanQuery["journey"];
  memberView?: boolean;
  task: Task;
};

const ExpertService = ({ handleEdit, hover = true, journey, memberView = true, task }: Props) => {
  const myClient = useMyClient();
  const myExpert = useMyExpert();
  const intl = useIntl();
  const resourceOpener = useOpener();
  const isArchived = task.archived || !!(task.goal && task.goal.archived);

  const isMemberView = !!(myClient || memberView);

  const status = useMemo(() => {
    if (!task.completed) {
      return JourneyStatusEnum.IN_PROGRESS;
    } else if (task.goal?.status === GoalStatusEnum.EDIT_MODE || task.goal?.hasDuplicateGoalInEditMode) {
      return JourneyStatusEnum.UPDATING;
    }
    return JourneyStatusEnum.COMPLETED;
  }, [task.completed, task.goal?.hasDuplicateGoalInEditMode, task.goal?.status]);

  const activeGoal = journey.goals?.find(goal => goal.id === task.goal?.activeGoalId);

  const hasEditModeChanges =
    task.goal?.status === GoalStatusEnum.EDIT_MODE &&
    (taskHasChanges(task, activeGoal) || task.resources.some(resource => resourceHasChanges(resource, activeGoal)));

  return (
    // eslint-disable-next-line react/forbid-elements
    <IonRouterLink
      className={status === JourneyStatusEnum.IN_PROGRESS || !memberView ? styles.noCursor : ""}
      data-test="expert-service"
      routerLink={
        status === JourneyStatusEnum.IN_PROGRESS || !hover
          ? undefined
          : journeyRoute({
              journeyId: journey.id,
              linkAfterJourney: `/client-visible-tasks/${task.id}`,
              myClient
            })
      }
    >
      <BasicCard
        border={hasEditModeChanges ? "danger-300" : undefined}
        hover={status === JourneyStatusEnum.IN_PROGRESS || !memberView ? "none" : "purple"}
        inactive={isArchived}
        padding="small"
      >
        <div className={`${styles.task} ${isMemberView ? styles.center : ""}`}>
          <div>
            <div className={styles.taskContent}>
              <ExpertAvatar expert={task.expert} />
              <BodyCopy color="gray">{task.title}</BodyCopy>
            </div>
            {myExpert && !isMemberView && (
              <div className={styles.taskBottomRow}>
                <div>
                  <Tooltip
                    content={
                      task.resources.length === 0
                        ? intl.formatMessage({ id: "pages.carePlan.base.expertServices.mustHaveResources" })
                        : undefined
                    }
                  >
                    {tooltipTriggerProps => (
                      <div className={`${styles.smallFlex} ${styles.noWrap}`} tabIndex={0} {...tooltipTriggerProps}>
                        <div className={styles.taskPopover}>
                          <Dot color={colorForStatus(status)} unread />
                          <BodyCopy color="gray-500" size="small">
                            <FormattedMessage id={`pages.carePlan.base.expertServices.statuses.${status}`} />
                          </BodyCopy>
                        </div>
                      </div>
                    )}
                  </Tooltip>
                </div>
                <div className={styles.buttonAndToggle}>
                  {task.resources.length > 0 ? (
                    <Button
                      routerLink={journeyRoute({
                        journeyId: journey.id,
                        linkAfterJourney: `/client-visible-tasks/${task.id}`,
                        myExpert
                      })}
                    >
                      <FormattedMessage id="pages.carePlan.base.expertServices.viewResource" />
                    </Button>
                  ) : (
                    <Button
                      fill="clear"
                      routerLink={journeyRoute({
                        journeyId: journey.id,
                        linkAfterJourney: `/client-visible-tasks/${task.id}`,
                        myExpert
                      })}
                    >
                      <IconBase icon="add" slot="start" />
                      <FormattedMessage id="pages.carePlan.base.expertServices.addResources" />
                    </Button>
                  )}
                  {task.resources.length > 0 && (
                    <div
                      aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.toggle" })}
                      className={styles.resourcesToggle}
                      onClick={resourceOpener.toggle}
                    >
                      <Icon icon={resourceOpener.isOpen ? "caretUp" : "caretDown"} size="small" />
                    </div>
                  )}
                </div>
                <div></div>
              </div>
            )}
          </div>

          {isMemberView && (
            <div>
              <div className={`${styles.noWrap} ${styles.statusWrapper}`}>
                <StatusText status={status} />
                {status !== JourneyStatusEnum.IN_PROGRESS && <Icon color="gray-500" icon="chevronRight" />}
              </div>
            </div>
          )}

          {myExpert && !isMemberView && (
            <div className={styles.notPublishedAndEdit}>
              <div className={styles.controls}>
                <TaskPopover handleEdit={handleEdit} journey={journey} task={task} />
                {task.resources.every(resource => resource.ownerViewedAt) && task.resources.length > 0 ? (
                  <StatusText iconOnly status={JourneyStatusEnum.VIEWED} />
                ) : null}
              </div>
            </div>
          )}
        </div>
        <div>
          <ExpandableSection isOpen={resourceOpener.isOpen}>
            <div className={styles.taskResources}>
              {sortByPosition(task.resources).map(resource => (
                <div className={styles.taskResource} key={resource.id}>
                  <BodyCopy color="gray">{resource.title}</BodyCopy>

                  {resource.ownerViewedAt ? <StatusText iconOnly status={JourneyStatusEnum.VIEWED} /> : null}
                </div>
              ))}
            </div>
          </ExpandableSection>
        </div>
      </BasicCard>
    </IonRouterLink>
  );
};

export default ExpertService;
